'use client';
import {
  SearchAnalyticsBoundary,
  SearchAnalyticsItem,
  type SearchAnalyticsData,
} from '@eliadmin/core/src/components/analytics/search-analytics-boundary';
import { searchSortOptions } from '@eliadmin/core/src/utils/search-sort';

import { buildProductsRequest } from '@eliadmin/core/src/api/products/product-list-request';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { PageHeader } from '@eliadmin/core/src/components/homepage/page-header';
import { ProductCard } from '@eliadmin/core/src/components/product/product-card';
import { Container } from '@eliadmin/core/src/components/ui/container';
import { Pagination } from '@eliadmin/core/src/components/ui/pagination';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import {
  buildProductsQuery,
  parseProductSearchParams,
} from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import type { ProductOrderBy } from '@eliadmin/core/src/utils/product-utils';
import { Suspense } from 'react';
import CategorySortToggleCustom from '../../components/custom/category-sort-toggle-custom';
import { TrackComponent } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import { ProductListSchema } from '@eliadmin/core/src/components/seo/product-list-schema';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';

export default function SearchPage() {
  const locale = useLocaleStore((s) => s.currentLocale);
  const { t } = useTranslations();
  const me = useCustomerStore((s) => s.me);
  const isInitialized = useCustomerStore((s) => s.isInitialized);
  const currency = useGlobalStore((s) => s.generalLayout?.currency);

  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const explicitOrderBy = searchParams.get('orderBy');
  const explicitOrder = searchParams.get('order');
  const publicSort = searchParams.get('sort');
  const selectedSortValue =
    publicSort === 'price'
      ? 'price:asc'
      : publicSort === '-price'
        ? 'price:desc'
        : explicitOrderBy && explicitOrder
          ? `${explicitOrderBy}:${explicitOrder}`
          : 'relevance';

  const onSearchSortChange = (value: string) => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete('sort');
    params.delete('sort[]');
    const nextValue = value === selectedSortValue ? '' : value;
    if (nextValue && nextValue !== 'relevance') {
      const [field, direction] = nextValue.split(':');
      if (!['name', 'price'].includes(field) || !['asc', 'desc'].includes(direction)) return;
      params.set('orderBy', field);
      params.set('order', direction);
    } else {
      params.delete('orderBy');
      params.delete('order');
    }
    params.delete('page');
    router.replace(`${pathname}?${params.toString()}`, { scroll: false });
  };

  const query = buildProductsQuery({
    ...parseProductSearchParams(searchParams),
    // The general parser does not include Wiltec's explicit A–Z option.
    ...(explicitOrderBy === 'name' ? { orderBy: 'name' as ProductOrderBy } : {}),
    results: 12,
    ...searchSortOptions(searchParams),
  });

  const productRequestUri = buildProductsRequest(query).uri;
  const productResetUri = buildProductsRequest({ ...query, page: undefined }).uri;

  const {
    data: products,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [CacheKey.PRODUCTS, locale, productRequestUri, me?.customer.id ?? 'guest'],
    queryFn: () =>
      fetchProducts({
        locale,
        query,
        authToken: me?.authToken,
      }),
    enabled: isInitialized,
  });

  return (
    <div>
      <PageHeader
        heading={t('text211')}
        description={
          searchParams.get('search') ? `${t('text211')}: ${searchParams.get('search')}` : ''
        }
        className={{ wrapper: 'bg-transparent' }}
      />
      <Container className="mt-4 w-full justify-start sm:flex">
        <CategorySortToggleCustom
          searchMode
          selectedValue={selectedSortValue}
          onSortChange={onSearchSortChange}
        />
      </Container>
      <Container className={cn('flex flex-col space-y-5 py-8')}>
        {/* <h3 className={cn('mb-4 text-3xl font-bold')}>{t('text1')}</h3> */}
        <div className="flex flex-col items-center gap-4">
          {isLoading ? (
            <div className={cn('flex h-[300px] w-full items-center justify-center')}>
              <Spinner />
            </div>
          ) : (
            <>
              {!!products?.data?.length && (
                <ProductListSchema products={products.data} currency={currency} />
              )}
              {!!products?.data?.length && (
                <TrackComponent
                  eventName={'view_item_list'}
                  eventParams={{
                    item_list_id: `search:${searchParams.get('search') ?? ''}`,
                    item_list_name: 'search',
                  }}
                  items={products.data}
                />
              )}
              <>
                <SearchAnalyticsBoundary
                  mode="submitted"
                  active={!isFetching}
                  data={{
                    manifest: products?.meta?.search,
                    identity: JSON.stringify([
                      locale,
                      buildProductsRequest(query).uri,
                      me?.customer.id ?? null,
                    ]),
                    locale,
                    results: (products?.data ?? []).map((product) => ({
                      type: 'product',
                      id: product.id,
                      href: product.slug,
                    })),
                    offset: products?.meta?.from
                      ? products.meta.from - 1
                      : (Number(query.page ?? 1) - 1) * Number(query.results ?? 12),
                  }}
                >
                  <div
                    className={cn(
                      'grid w-full grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
                    )}
                  >
                    {products?.data.map((product, searchIndex) => (
                      <SearchAnalyticsItem index={searchIndex} key={searchIndex}>
                        {<ProductCard key={`product-${product.id}`} product={product} />}
                      </SearchAnalyticsItem>
                    ))}
                  </div>
                </SearchAnalyticsBoundary>
              </>
            </>
          )}
          <Suspense>
            <Pagination links={products?.meta?.links ?? []} resetWhen={[productResetUri]} />
          </Suspense>
        </div>
      </Container>
    </div>
  );
}
